import React from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { CheckCircle2Icon, CircleDashedIcon, PrinterIcon, UploadIcon } from "lucide-react";
import { useHackathon } from "../contexts/HackathonContext";
import { groups } from "../data/groups";
import { schedule } from "../data/schedule";
import { PageHeader } from "../components/PageHeader";
import { ProgressReportTable } from "../components/ProgressReportTable";
import { currentProgramDay, demoCountdown, formatDay, PROGRAM_DAYS } from "../utils/dates";
export function Dashboard() {
  const {
    state
  } = useHackathon();
  const day = currentProgramDay();
  const inProgram = day >= 1 && day <= PROGRAM_DAYS;
  const scheduleDay = schedule.find(s => s.day === Math.min(Math.max(day, 1), PROGRAM_DAYS + 1));
  const countdown = demoCountdown();
  const todaysVideos = state.videos.filter(v => v.day === day);
  return <main className="mx-auto max-w-6xl px-5 py-10 md:px-8 md:py-12" data-id="element-768" data-mp="1retqzzh8xu21">
      <PageHeader title="Overview" description="Daily check-ins, team progress and the road to Demo Day." actions={<button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl border border-ink/15 bg-surface px-4 py-2.5 text-sm font-semibold transition-colors duration-150 hover:bg-paper" data-id="element-770" data-mp="26ngfq905g1h1">
            <PrinterIcon className="h-4 w-4" aria-hidden="true" data-id="element-771" data-mp="1j08djzy0p8j8" />
            Print report
          </button>} data-id="element-769" data-mp="21pnabi6o7bht" />

      <section className="bg-waves mt-8 grid gap-8 overflow-hidden rounded-2xl px-6 py-8 text-white md:grid-cols-[1fr_auto] md:px-10" data-id="element-772" data-mp="1nyi4tzgmfozk">
        <div data-id="element-773" data-mp="1wqbqu1ex5rwj">
          <p className="text-sm font-semibold text-bryellow" data-id="element-774" data-mp="1wr3p5jzwi3qi">
            {inProgram ? `Day ${day} of ${PROGRAM_DAYS} · ${formatDay(day, "EEEE, MMM d")}` : day < 1 ? "Starting October 7" : day === PROGRAM_DAYS + 1 ? "Today is Demo Day" : "Program complete"}
          </p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl" data-id="element-775" data-mp="27iu0wx0a0kbw">{scheduleDay?.theme}</h2>
          {inProgram && scheduleDay?.videoPrompt && <p className="mt-3 max-w-lg text-[15px] text-white/75" data-id="element-776" data-mp="2h9op46x31tzz">Today's video: {scheduleDay.videoPrompt}</p>}
        </div>
        <div className="md:text-right" data-id="element-777" data-mp="2ce9b9ez2ry3k">
          <p className="text-sm text-white/60" data-id="element-778" data-mp="1olj2tfqdrwy1">{countdown.done ? "Demo Day" : "Until Demo Day"}</p>
          {countdown.done ? <p className="mt-1 text-3xl font-bold" data-id="element-779" data-mp="1josooxy4zbf9">It's live</p> : <p className="mt-1 flex items-baseline gap-3 tabular-nums md:justify-end" data-id="element-780" data-mp="1xo20llfb7jdf">
              <span className="text-5xl font-bold" data-id="element-781" data-mp="1tl5pefs3mpji">{countdown.days}</span>
              <span className="text-sm text-white/60" data-id="element-782" data-mp="391w3t1t12wbq">days</span>
              <span className="text-5xl font-bold" data-id="element-783" data-mp="390cp3fn0stc2">{countdown.hours}</span>
              <span className="text-sm text-white/60" data-id="element-784" data-mp="34zspm5ez5dmr">hrs</span>
            </p>}
          <p className="mt-1 text-sm text-white/60" data-id="element-785" data-mp="3cj1zs0vb1gcv">Tue, Oct 13 · 14:00</p>
        </div>
      </section>

      <div className="mt-10 grid gap-10 lg:grid-cols-3" data-id="element-786" data-mp="3hk7xkd3tntt9">
        <section aria-labelledby="checkin-heading" className="lg:col-span-2" data-id="element-787" data-mp="32lxe025dfmwl">
          <div className="flex items-baseline justify-between pb-3" data-id="element-788" data-mp="32o0e5f08mwf1">
            <h2 id="checkin-heading" className="text-lg font-semibold" data-id="element-789" data-mp="37ls5a6t26ei6">
              Today's video check-in
            </h2>
            {inProgram && <p className="text-sm tabular-nums text-muted" data-id="element-790" data-mp="440euwukm2m3">
                {todaysVideos.length}/{groups.length} submitted · due 21:00
              </p>}
          </div>
          {!inProgram ? <p className="rounded-xl border border-line bg-surface px-5 py-6 text-sm text-muted" data-id="element-791" data-mp="3qjcvbh7k7j4d">
              Daily videos run from October 7 to 12. See each group's full log on the Groups page.
            </p> : <ul className="divide-y divide-line rounded-xl border border-line bg-surface" data-id="element-792" data-mp="mvjequtat0jw">
              {groups.map(g => {
            const video = todaysVideos.find(v => v.groupId === g.id);
            const members = state.students.filter(s => s.groupId === g.id);
            return <li key={g.id} className="flex items-center gap-4 px-5 py-3.5" data-id="element-793" data-mp="2ldwpg99maekd">
                    {video ? <CheckCircle2Icon className="h-5 w-5 shrink-0 text-brgreen" aria-hidden="true" data-id="element-794" data-mp="2ggxvprtv89mp" /> : <CircleDashedIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" data-id="element-795" data-mp="392pbne0ft81s" />}
                    <div className="min-w-0 flex-1" data-id="element-796" data-mp="34bh28hz3xmom">
                      <p className="text-[15px] font-semibold" data-id="element-797" data-mp="34a61kr0ykv1q">Group {g.id}</p>
                      <p className="truncate text-sm text-muted" data-id="element-798" data-mp="2zd5v4f075qa3">{members.map(m => m.name.split(" ")[0]).join(", ")}</p>
                    </div>
                    {video ? <span className="text-sm text-muted" data-id="element-799" data-mp="3s1bg92v5xhag">Submitted {format(new Date(video.uploadedAt), "HH:mm")}</span> : <Link to={`/organizer/groups/${g.id}`} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-ink px-3 py-1.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-ink/85" data-id="element-800" data-mp="86yh4a095ezu">
                        <UploadIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-801" data-mp="n4gzmumc6vyu" />
                        Upload
                      </Link>}
                  </li>;
          })}
            </ul>}
        </section>

        <section aria-labelledby="today-heading" data-id="element-802" data-mp="vxatbx2j5xw8">
          <div className="flex items-baseline justify-between pb-3" data-id="element-803" data-mp="10w5f97vso2kk">
            <h2 id="today-heading" className="text-lg font-semibold" data-id="element-804" data-mp="15up014ynj8bc">
              Today's schedule
            </h2>
            <Link to="/organizer/program" className="text-sm text-muted transition-colors duration-150 hover:text-ink" data-id="element-805" data-mp="1atjq8ryy7z5h">
              Full schedule
            </Link>
          </div>
          <ol className="space-y-3 border-l-2 border-bryellow pl-4" data-id="element-806" data-mp="g6kz67l331u6">
            {scheduleDay?.sessions.map(s => <li key={s.time + s.title} data-id="element-807" data-mp="uvz9bhwhhnam">
                <p className="text-xs font-semibold tabular-nums text-muted" data-id="element-808" data-mp="ux0jlk5oe5ra">{s.time}</p>
                <p className="text-[15px] font-medium" data-id="element-809" data-mp="q3z4bb1k4476">{s.title}</p>
                {s.detail && <p className="text-sm text-muted" data-id="element-810" data-mp="13vchm78vg5tr">{s.detail}</p>}
              </li>)}
          </ol>
        </section>
      </div>

      <section aria-labelledby="report-heading" className="mt-12" data-id="element-811" data-mp="yz5c2fke0wcc">
        <h2 id="report-heading" className="pb-3 text-lg font-semibold" data-id="element-812" data-mp="1dta3wp767sjq">
          Progress report
        </h2>
        <ProgressReportTable data-id="element-813" data-mp="18vrw524w3jdb" />
      </section>
    </main>;
}